// SPDX-License-Identifier: AGPL-3.0-or-later

import type {StatusType} from '@fluxer/constants/src/StatusConstants';
import {StatusTypes} from '@fluxer/constants/src/StatusConstants';
import {makeAutoObservable, observable, runInAction} from 'mobx';

const PRESENCE_TTL_MS = 5 * 60 * 1000;
const CLEANUP_INTERVAL_MS = 60 * 1000;

interface TransientPresence {
	status: StatusType;
	timestamp: number;
}

class TransientPresenceRegistryClass {
	presences = observable.map<string, TransientPresence>();
	private cleanupInterval: NodeJS.Timeout | null = null;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		this.startCleanup();
	}

	private startCleanup(): void {
		if (this.cleanupInterval) return;
		this.cleanupInterval = setInterval(() => {
			this.pruneStale();
		}, CLEANUP_INTERVAL_MS);
	}

	private pruneStale(): void {
		const now = Date.now();
		runInAction(() => {
			for (const [userId, presence] of this.presences) {
				if (now - presence.timestamp > PRESENCE_TTL_MS) {
					this.presences.delete(userId);
				}
			}
		});
	}

	updatePresence(userId: string, status: StatusType): void {
		this.presences.set(userId, {
			status,
			timestamp: Date.now(),
		});
	}

	updatePresences(
		presences: Array<{
			userId: string;
			status: StatusType;
		}>,
	): void {
		runInAction(() => {
			const now = Date.now();
			for (const {userId, status} of presences) {
				this.presences.set(userId, {status, timestamp: now});
			}
		});
	}

	getStatus(userId: string): StatusType {
		const transient = this.presences.get(userId);
		if (transient && Date.now() - transient.timestamp <= PRESENCE_TTL_MS) {
			return transient.status;
		}
		return StatusTypes.OFFLINE;
	}

	getTransientStatus(userId: string): StatusType | null {
		const transient = this.presences.get(userId);
		if (transient && Date.now() - transient.timestamp <= PRESENCE_TTL_MS) {
			return transient.status;
		}
		return null;
	}

	hasTransientPresence(userId: string): boolean {
		const transient = this.presences.get(userId);
		return transient != null && Date.now() - transient.timestamp <= PRESENCE_TTL_MS;
	}

	clearPresence(userId: string): void {
		this.presences.delete(userId);
	}

	clear(): void {
		this.presences.clear();
	}

	cleanup(): void {
		if (this.cleanupInterval) {
			clearInterval(this.cleanupInterval);
			this.cleanupInterval = null;
		}
		this.presences.clear();
	}
}

export default new TransientPresenceRegistryClass();
